import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDiscountListData,
  modifyDiscountStatus,
  setDiscountFormData,
  setFlag,
} from "../../features/discountSlice";
import Spinner from "../../components/spinner/Spinner";
import Button from "../../components/button/Button";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import { largeModalShowHide, setTitle } from "../../features/modalSlice";
import DiscountModal from "./DiscountModal";
import DiscountDetailsModal from "./DiscountDetailsModal";
import { LargeModal } from "../../components/modal/ModalUI";
import { errorToastify } from "../../utils/toastify.util";
import { can } from "../../utils/permission.util";
import {
  RowActionButton,
  RowActionIcons,
  RowActions,
} from "../../components/data-table/RowActions";

const discountFormData = {
  id: "",
  discount_code: "",
  discount_name: "",
  discount_type: "0",
  amount: "",
  // discount used-for choice is removed from the form; default to Users
  isAdmin: "0",
  // multi-select, one array per pack type
  item_pack_ids: [],
  hunt_pack_ids: [],
  quest_pack_ids: [],
  max_users: "",
  validity_days: "",
  start_date: "",
  end_date: "",
};

const processingStatuses = {
  pending: { label: "Pending", color: "warning text-dark" },
  inprocess: { label: "In Process", color: "info text-dark" },
  completed: { label: "Completed", color: "success" },
  error: { label: "Error", color: "danger" },
};

const formatProcessingError = (reason) => {
  if (!reason) return "";
  // Parent errors may contain one JSON object per failed pack.
  return String(reason).split("\n").map((line) => {
    try {
      const notes = JSON.parse(line);
      if (!notes || typeof notes !== "object") return line;
      return Object.entries(notes).map(([store, note]) => {
        const label = store === "apple" ? "App Store" : store === "google" ? "Google Play" : store;
        return `${label}: ${typeof note === "string" ? note : note?.message || "Processing failed"}`;
      }).join("\n");
    } catch {
      return line;
    }
  }).join("\n");
};

const DiscountProcessingStatus = ({ discount }) => {
  const status = processingStatuses[discount.processing_status] || {
    label: "Not Available", color: "secondary",
  };
  const counts = discount.processing_counts;
  const error = formatProcessingError(discount.processing_error_reason);

  return (
    <div style={{ minWidth: "150px", maxWidth: "300px" }}>
      <span className={`badge bg-${status.color}`}>{status.label}</span>
      {counts && (
        <div className="small mt-1">
          <div>{counts.completed ?? 0} / {counts.total ?? 0} packs completed</div>
          {Number(counts.pending) > 0 && <div>{counts.pending} pending</div>}
          {Number(counts.inprocess) > 0 && <div>{counts.inprocess} in process</div>}
          {Number(counts.error) > 0 && <div className="text-danger">{counts.error} failed</div>}
          {Number(counts.needs_reconciliation) > 0 && (
            <div className="text-warning">{counts.needs_reconciliation} require review before processing</div>
          )}
        </div>
      )}
      {error && (
        <details className="small text-start mt-1">
          <summary className="text-danger" style={{ cursor: "pointer" }}>Error details</summary>
          <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{error}</div>
        </details>
      )}
    </div>
  );
};

const DiscountList = () => {
  const dispatch = useDispatch();
  const { listData, isLoad } = useSelector((state) => state.discount);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [modalMode, setModalMode] = useState("form");
  const [selectedDiscount, setSelectedDiscount] = useState(null);

  useEffect(() => {
    dispatch(fetchDiscountListData());
  }, [dispatch]);

  const typeValue = (value) => {
    let str = "";
    if (value === "1") {
      str = "Percentage";
    } else {
      str = "Flat";
    }
    return str;
  };

  const getUsedFor = (value) => {
    let str = "";
    if (value === "1") {
      str = "Admin";
    } else {
      str = "Users";
    }
    return str;
  };

  const editDiscount = (id) => {
    try {
      if (id) {
        const list =
          listData && listData.length
            ? listData.find((list) => list.id === id)
            : null;
        if (!list) {
          throw new Error(" any user not found");
        }

        const formData = {
          id: id,
          discount_code: list?.discount_code || "",
          discount_name: list?.discount_name || "",
          discount_type: list?.discount_type || "",
          amount: list?.amount || "",
          isAdmin: list?.isAdmin || "",
          // itemgroups: [{ pack_item_id, pack_type, group_name }] — split by
          // pack type into item_pack_ids/hunt_pack_ids/quest_pack_ids in the slice
          itemgroups: list?.itemgroups || [],
          max_users: list?.max_users || "",
          validity_days: list?.validity_days || "",
          start_date: list?.startdate || "",
          end_date: list?.enddate || "",
        };

        setModalMode("form");
        dispatch(setFlag("update"));
        dispatch(setDiscountFormData(formData));
        dispatch(largeModalShowHide());
        dispatch(setTitle("Edit Discount"));
      } else {
        throw new Error(" any discount data not found");
      }
    } catch (err) {
      errorToastify(err.message);
    }
  };

  const viewDetails = (id) => {
    try {
      const list =
        listData && listData.length
          ? listData.find((list) => list.id === id)
          : null;
      if (!list) {
        throw new Error("Discount not found");
      }

      setSelectedDiscount(list);
      setModalMode("details");
      dispatch(largeModalShowHide());
      dispatch(setTitle("Discount Details"));
    } catch (err) {
      errorToastify(err.message);
    }
  };

  const addItemHandler = () => {
    setModalMode("form");
    dispatch(setFlag("add"));
    dispatch(setDiscountFormData(discountFormData));
    dispatch(largeModalShowHide());
    dispatch(setTitle("Add Discount"));
  };

  // Toggle status handler
  const toggleStatus = (id, status) => {
    // console.log("Discount Id >>>> ", id)
    let str = "";
    if (status === "0") {
      str = "1";
    } else {
      str = "0";
    }

    let req = {
      id: id,
      status: str,
    };

    dispatch(modifyDiscountStatus(req));
  };

  const sortedListData = [...(listData || [])].sort((a, b) => {
    const { key, direction } = sortConfig;

    if (!key) return 0;

    let aVal = a[key];
    let bVal = b[key];

    // Normalize for string comparison
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  // Show item group names in comma separated format with proper checks
  const getItemGroupNames = (itemgroups) => {
    if (!Array.isArray(itemgroups) || itemgroups.length === 0) return null;

    const names = itemgroups
      .filter((item) => item && item.group_name)
      .map((item) => item.group_name);

    return names.length ? names.join(", ") : null;
  };


const normalizeDateForInput = (value) => {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (str.includes("T")) return str.split("T")[0];

  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0];
};

  return (
    <div id="main" className="main">
      <Breadcrumb page="Discount List" />
      <section className="section">
        <div className="row mb-3">
          <div className="col-sm-6 col-md-12 col-lg-12 d-flex justify-content-between align-items-center">
            <h6 className="card-title">Discount List</h6>
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary me-2"
                disabled={isLoad}
                onClick={() => dispatch(fetchDiscountListData())}
              >
                Refresh Status
              </button>
              {can("/discount", "create") && (
                <Button
                  className="btn btn-sm btn-outline-primary me-2"
                  title="Create Discount"
                  onClick={() => addItemHandler()}
                >
                  <i className="fa-solid fa-plus" />
                </Button>
              )}
            </div>
          </div>
          {isLoad ? (
            <Spinner />
          ) : (
            <table className="table table-bordered">
              <thead>
                <tr>
                 
                  <th
                    onClick={() => handleSort("discount_code")}
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                    Discount Code{" "}
                    {sortConfig.key === "discount_code"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    onClick={() => handleSort("discount_name")}
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                    Discount Name{" "}
                    {sortConfig.key === "item_name"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    onClick={() => handleSort("discount_name")}
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                    Pack Name{" "}
                    {sortConfig.key === "item_name"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    onClick={() => handleSort("amount")}
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                    Discount Value{" "}
                    {sortConfig.key === "amount"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                   Start Date
                  </th>
                      <th
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                   End Date
                  </th>
                  <th
                    onClick={() => handleSort("discount_type")}
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                    Type{" "}
                    {sortConfig.key === "discount_type"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  
                  <th
                    onClick={() => handleSort("isAdmin")}
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                    Used For{" "}
                    {sortConfig.key === "isAdmin"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    className="text-center fs-7"
                    style={{ cursor: "pointer" }}
                  >
                    Status{" "}
                    {sortConfig.key === "status"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Cron Status
                  </th>
                  {(can("/discount", "create") ||
                    can("/discount", "delete")) && (
                    <th scope="col" className="text-center fs-7" width="15%">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedListData && sortedListData.length
                  ? sortedListData.map((list, i) => (
                      <tr key={`sortedListData-${i}`}>
                        
                        <td className="text-center fs-7 align-middle">
                          {list?.discount_code || null}
                        </td>
                        <td className="text-center fs-7 align-middle">
                          {list?.discount_name || null}
                        </td>
                        <td className="text-center fs-7 align-middle">
                          {getItemGroupNames(list?.itemgroups)}
                        </td>
                        <td className="text-center fs-7 align-middle">
                          {list?.amount || null}
                        </td>
                         <td className="text-center fs-7 align-middle">
                          {normalizeDateForInput(list?.startdate) || null}
                        </td> 
                        <td className="text-center fs-7 align-middle">
                          {normalizeDateForInput(list?.enddate) || null}
                        </td>
                        <td className="text-center fs-7 align-middle">
                          {typeValue(list.discount_type)}
                        </td>
                        
                        <td className="text-center fs-7 align-middle">
                          {getUsedFor(list.isAdmin)}
                        </td>
                        <td className="text-center fs-7 align-middle">
                          <button
                            disabled={!can("/discount", "create")}
                            className={`btn btn-${list.status === "1" ? "success" : "danger"}`}
                            onClick={() => toggleStatus(list.id, list.status)}
                          >
                            {list.status === "1" ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="text-center fs-7 align-middle">
                          <DiscountProcessingStatus discount={list} />
                        </td>
                        {(can("/discount", "create") ||
                          can("/discount", "delete")) && (
                          <td className="text-center fs-7 align-middle">
                            <RowActions>
                              <RowActionButton
                                label="View"
                                iconClass={RowActionIcons.view}
                                onClick={() => viewDetails(list?.id || null)}
                              />
                              {can("/discount", "create") && (
                                <RowActionButton
                                  label="Edit"
                                  iconClass={RowActionIcons.edit}
                                  variant="outline-success"
                                  onClick={() => editDiscount(list?.id || null)}
                                />
                              )}
                            </RowActions>
                          </td>
                        )}
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>
          )}
        </div>
      </section>
      <LargeModal>
        {modalMode === "details" ? (
          <DiscountDetailsModal discount={selectedDiscount} />
        ) : (
          <DiscountModal />
        )}
      </LargeModal>
    </div>
  );
};

export default DiscountList;
